import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import EyeMark from "@/components/EyeMark";

const approach = [
    {
        num: "01",
        title: "Research first",
        text: "We map the market and understand the talent landscape before we start searching — so every approach is informed, not speculative.",
    },
    {
        num: "02",
        title: "Precision over volume",
        text: "Every candidate we present is vetted against both role requirements and organisational fit. Short shortlists, not long ones.",
    },
    {
        num: "03",
        title: "Confidentiality and discretion",
        text: "Non-negotiable on every engagement — and especially critical for senior and leadership-level searches.",
    },
    {
        num: "04",
        title: "Partner-led delivery",
        text: "Every engagement is personally overseen by our founding partners, never handed off to junior teams.",
    },
];

export default function About() {
    return (
        <div>
            <Seo
                title="About Us | Vantage Crest Research — Boutique Talent Intelligence"
                description="Vantage Crest Research is a boutique executive search and talent intelligence firm in Mumbai. Our mission, vision and research-first approach."
            />

            <section
                className="relative overflow-hidden bg-[#0C0C0C] pt-40 pb-24"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 85% 10%, rgba(18,18,18,0.8) 0%, rgba(6,6,6,0.95) 55%, #060606 100%)",
                }}
            >
                <div className="absolute inset-0 grid-lines-dark pointer-events-none" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <Eyebrow dark>About the Firm</Eyebrow>
                    <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-[#F7F5F1] mt-6 max-w-4xl">
                        <span className="block overflow-hidden pb-1">
                            <span className="block animate-[heroLine_1s_cubic-bezier(0.16,1,0.3,1)_both]">
                                Better hiring decisions
                            </span>
                        </span>
                        <span className="block overflow-hidden pb-1">
                            <span
                                className="block animate-[heroLine_1s_cubic-bezier(0.16,1,0.3,1)_both]"
                                style={{ animationDelay: "0.14s" }}
                            >
                                start with <span className="text-[#D8BC7A]">better research.</span>
                            </span>
                        </span>
                    </h1>
                    <style>{`@keyframes heroLine { from { transform: translateY(112%);} to { transform: translateY(0);} }`}</style>
                    <p className="mt-8 text-lg text-[#A6A69C] leading-relaxed max-w-2xl">
                        A boutique executive search and talent intelligence firm founded on
                        one principle — and built like a research house.
                    </p>
                </div>
            </section>

            <section className="bg-white py-24 grid-lines-light">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
                    <Reveal className="lg:col-span-4">
                        <EyeMark size={110} className="mb-9" />
                        <Eyebrow>Our Origin</Eyebrow>
                        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#141414] mt-4 leading-snug">
                            An extension of your talent team.
                        </h2>
                    </Reveal>
                    <div className="lg:col-span-8 space-y-6">
                        <Reveal delay={0.1}>
                            <p className="text-base text-[#4E4E48] leading-relaxed">
                                Vantage Crest Research is a boutique executive search and talent
                                intelligence firm founded on one principle: better hiring
                                decisions start with better research. We work as an extension of
                                our clients' talent teams — mapping markets, building candidate
                                pipelines, and executing confidential senior-level searches with
                                the rigour of a research house and the responsiveness of a
                                dedicated search partner.
                            </p>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <p className="text-base text-[#4E4E48] leading-relaxed">
                                Our team brings deep, hands-on delivery experience across BFSI,
                                Pharma, IT, Retail, and Manufacturing, built through years of
                                managing complex, cross-border hiring mandates for global
                                executive search firms and enterprise clients. We don't just
                                fill roles — we build the talent intelligence our clients need
                                to make confident hiring decisions today and plan ahead for
                                tomorrow.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-[#F7F5F1] border-y border-[#E7E4DC] py-24">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-6">
                    <Reveal className="h-full">
                        <div className="bg-white border border-[#E7E4DC] hover:border-[#C9A24B]/50 transition-colors duration-300 p-10 h-full">
                            <p className="font-mono-accent text-xs tracking-[0.28em] uppercase text-[#C9A24B]">
                                Our Mission
                            </p>
                            <p className="font-display text-xl sm:text-2xl text-[#141414] leading-relaxed mt-5">
                                To be the talent research and executive search partner
                                organisations turn to when hiring decisions matter most —
                                combining data-driven market intelligence with hands-on search
                                execution to deliver talent that fits, not just talent that's
                                available.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12} className="h-full">
                        <div className="bg-[#171717] border border-white/10 p-10 h-full">
                            <p className="font-mono-accent text-xs tracking-[0.28em] uppercase text-[#D8BC7A]">
                                Our Vision
                            </p>
                            <p className="font-display text-xl sm:text-2xl text-[#F6F4EF] leading-relaxed mt-5">
                                To change how organisations think about talent acquisition —
                                from a reactive, vacancy-driven activity into a proactive,
                                research-led discipline that gives businesses a lasting
                                competitive edge.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-[#0C0C0C] grid-lines-dark py-24">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal className="max-w-2xl mb-14">
                        <Eyebrow dark>Our Approach</Eyebrow>
                        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#F6F4EF] mt-4 leading-snug">
                            Four principles. No exceptions.
                        </h2>
                    </Reveal>
                    <div className="border-t border-white/10">
                        {approach.map((a, i) => (
                            <Reveal key={a.num} delay={i * 0.06}>
                                <div className="grid sm:grid-cols-12 gap-4 sm:gap-8 py-9 border-b border-white/10 items-baseline group">
                                    <span className="sm:col-span-2 font-mono-accent text-sm text-[#C9A24B] tracking-widest">
                                        {a.num}
                                    </span>
                                    <h3 className="sm:col-span-4 font-display text-xl sm:text-2xl font-semibold text-[#F6F4EF] group-hover:text-[#D8BC7A] transition-colors duration-300">
                                        {a.title}
                                    </h3>
                                    <p className="sm:col-span-6 text-sm text-[#A6A69C] leading-relaxed">
                                        {a.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <Link
                            to="/contact"
                            data-testid="about-cta-partner"
                            className="mt-12 inline-flex items-center gap-2 bg-[#C9A24B] hover:bg-[#D8BC7A] text-[#0C0C0C] font-semibold px-7 py-3.5 text-sm transition-colors duration-200 active:scale-[0.98]"
                        >
                            Start a Conversation <ArrowUpRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
